import { UserPlus, Users, X } from 'lucide-react'
import type { UseCompanyInfoTabReturn } from '@/features/company/types'
import styles from '../../../styles/companyAdd.module.css'
import { SectionCard } from '@/components/ui/SectionCard'
import { cn } from '@/lib/utils/helpers'

export function CompanyInfoManagementSection({
    register,
    watch,
    canEditInfo,
    newOwnerName,
    setNewOwnerName,
    ownersList,
    ownersLoading,
    addOwner,
    deleteOwner,
}: UseCompanyInfoTabReturn) {
    const managedBy = watch('company_managed')

    return (
        <SectionCard
            icon={Users}
            title="Management"
            testId="company-info-section-management"
        >
            <div className="space-y-6">
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Company managed by?</label>
                    <div className="mt-2 flex flex-wrap gap-3">
                        {(
                            [
                                { value: 'ASSET', label: 'Asset', testId: 'company-info-company-managed-asset-radio' },
                                { value: 'FEE', label: 'Fee', testId: 'company-info-company-managed-fee-radio' },
                            ] as const
                        ).map((option) => {
                            const selected = managedBy === option.value
                            return (
                                <label
                                    key={option.value}
                                    className={cn(
                                        'inline-flex min-w-[120px] cursor-pointer items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium transition-colors',
                                        selected
                                            ? 'border-[rgb(21_151_255)] bg-[#e8f4ff] text-[#0c4a6e]'
                                            : 'border-slate-200 bg-white text-[#253c5c] hover:border-slate-300',
                                        !canEditInfo && 'cursor-not-allowed opacity-70',
                                    )}
                                >
                                    <input
                                        type="radio"
                                        value={option.value}
                                        disabled={!canEditInfo}
                                        {...register('company_managed')}
                                        data-testid={option.testId}
                                        className="h-4 w-4 accent-[rgb(21_151_255)]"
                                    />
                                    {option.label}
                                </label>
                            )
                        })}
                    </div>
                </div>

                {managedBy === 'FEE' && (
                    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5">
                        <div className="space-y-2">
                            <label className={styles.formLabel} htmlFor="company-info-new-owner-name-input">
                                Owner name
                            </label>
                            <div className="flex flex-wrap items-center gap-3">
                                <input
                                    id="company-info-new-owner-name-input"
                                    type="text"
                                    placeholder="Enter owner name"
                                    disabled={!canEditInfo || ownersLoading}
                                    value={newOwnerName}
                                    onChange={(e) => setNewOwnerName(e.target.value)}
                                    data-testid="company-info-new-owner-name-input"
                                    className={cn(styles.customFormControl, 'w-full max-w-[280px]')}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            addOwner()
                                        }
                                    }}
                                />
                                {canEditInfo && (
                                    <button
                                        type="button"
                                        data-testid="company-info-add-owner-button"
                                        className={styles.btnPrimary}
                                        disabled={ownersLoading || !newOwnerName.trim()}
                                        onClick={(e) => addOwner(e)}
                                    >
                                        <UserPlus className="h-4 w-4" aria-hidden />
                                        {ownersLoading ? 'Adding…' : 'Add Owner'}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="mt-5">
                            <div className="mb-3 flex items-center justify-between gap-3">
                                <h5 className="text-sm font-semibold text-[#253c5c]">Owners list</h5>
                                <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-[#778aa4] ring-1 ring-slate-200">
                                    {ownersList.length} {ownersList.length === 1 ? 'owner' : 'owners'}
                                </span>
                            </div>

                            {ownersLoading && ownersList.length === 0 ? (
                                <p
                                    data-testid="company-info-owners-list-loading"
                                    className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-[#778aa4]"
                                >
                                    Loading owners…
                                </p>
                            ) : ownersList.length > 0 ? (
                                <ul className="flex flex-wrap gap-2">
                                    {ownersList.map((owner, idx) => {
                                        const name = String(owner.company_owner || '').trim() || '—'
                                        const initial = name.charAt(0).toUpperCase()
                                        return (
                                            <li
                                                key={String(owner.owner_id ?? idx)}
                                                className="inline-flex max-w-full items-center gap-2 rounded-lg border border-slate-200 bg-white py-1.5 pl-2 pr-1 shadow-sm"
                                            >
                                                <span
                                                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e8f4ff] text-xs font-semibold text-[rgb(21_151_255)]"
                                                    aria-hidden
                                                >
                                                    {initial}
                                                </span>
                                                <span className="min-w-0 truncate text-sm font-medium text-[#253c5c]">
                                                    {name}
                                                </span>
                                                {canEditInfo && owner.owner_id != null && owner.owner_id !== '' && (
                                                    <button
                                                        type="button"
                                                        disabled={ownersLoading}
                                                        data-testid={`company-info-owner-row-${owner.owner_id}-delete-button`}
                                                        onClick={() => deleteOwner(Number(owner.owner_id))}
                                                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#e74c3c] transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                                                        title="Delete Owner"
                                                        aria-label={`Delete owner ${name}`}
                                                    >
                                                        <X className="h-3.5 w-3.5" strokeWidth={2.25} />
                                                    </button>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ul>
                            ) : (
                                <p
                                    data-testid="company-info-owners-list-empty"
                                    className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-6 text-center text-sm text-[#778aa4]"
                                >
                                    No owners added yet.
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </SectionCard>
    )
}
